import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Mail, Zap, Shield, Bell, Loader2, Plus, Trash2, Link2, Unlink, RefreshCw, Server, Pencil, Eye, EyeOff, Wifi, WifiOff, AlertTriangle, Database, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, token, login: authLogin } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    global_auto_respond: false,
    high_urgency_alerts: true
  });

  // Gmail Accounts state
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccountEmail, setNewAccountEmail] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  const [connectingAccount, setConnectingAccount] = useState(null);
  // Gmail Pub/Sub Push health state
  const [pushHealth, setPushHealth] = useState(null);
  const [loadingPushHealth, setLoadingPushHealth] = useState(true);
  const [renewingWatches, setRenewingWatches] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [gmailAccountsOpen, setGmailAccountsOpen] = useState(false);
  const [gmailPushOpen, setGmailPushOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', current_password: '', new_password: '', confirm_password: '' });
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  // VDP Cache state
  const [cacheVin, setCacheVin] = useState('');
  const [clearingCache, setClearingCache] = useState(false);
  const [clearingAllCache, setClearingAllCache] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchGmailAccounts();
    fetchPushHealth();

    const connected = searchParams.get('gmail_account_connected');
    const error = searchParams.get('error');
    if (connected === 'true') {
      toast.success('Gmail account authorized successfully!');
      setSearchParams({});
      fetchGmailAccounts();
      fetchPushHealth();
    } else if (error) {
      const msgs = {
        invalid_state: 'Invalid OAuth state. Please try again.',
        state_expired: 'OAuth session expired. Please try again.',
        oauth_failed: 'Failed to connect Gmail. Please try again.'
      };
      toast.error(msgs[error] || 'Gmail connection failed');
      setSearchParams({});
    }
    // Auto-refresh push health every 60s so the panel stays live
    const iv = setInterval(fetchPushHealth, 60000);
    return () => clearInterval(iv);
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers, withCredentials: true });
      setSettings({
        global_auto_respond: res.data.global_auto_respond || false,
        high_urgency_alerts: res.data.high_urgency_alerts !== false
      });
    } catch {
      console.log('Using default settings');
    }
    setLoading(false);
  };

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, newSettings, { headers, withCredentials: true });
      toast.success('Settings saved');
    } catch {
      setSettings(settings);
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const fetchGmailAccounts = async () => {
    try {
      const res = await axios.get(`${API}/gmail-accounts`, { headers, withCredentials: true });
      setGmailAccounts(res.data);
    } catch {
      console.log('Failed to fetch Gmail accounts');
    }
    setLoadingAccounts(false);
  };

  const fetchPushHealth = async () => {
    try {
      const res = await axios.get(`${API}/gmail/watch/health`, { headers, withCredentials: true });
      setPushHealth(res.data);
    } catch {
      setPushHealth(null);
    }
    setLoadingPushHealth(false);
  };

  const renewAllWatches = async () => {
    setRenewingWatches(true);
    try {
      const res = await axios.post(`${API}/gmail/watch/renew-all`, {}, { headers, withCredentials: true });
      const renewed = res.data.renewed || 0;
      const failed = (res.data.results || []).filter(r => r.status !== 'renewed').length;
      if (renewed > 0 && failed === 0) {
        toast.success(`Renewed ${renewed} watch${renewed > 1 ? 'es' : ''}`);
      } else if (renewed > 0) {
        toast.warning(`Renewed ${renewed}, ${failed} failed`);
      } else {
        toast.error('No watches could be renewed — check account status');
      }
      fetchPushHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renewal failed');
    }
    setRenewingWatches(false);
  };

  const addGmailAccount = async () => {
    if (!newAccountEmail.trim()) return;
    setAddingAccount(true);
    try {
      await axios.post(`${API}/gmail-accounts`, { email: newAccountEmail.trim() }, { headers, withCredentials: true });
      toast.success('Processing account added');
      setNewAccountEmail('');
      setShowAddAccount(false);
      fetchGmailAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add account');
    }
    setAddingAccount(false);
  };

  const deleteGmailAccount = async (accountId) => {
    if (!window.confirm('Remove this processing account? Dealerships using it will need to be reassigned.')) return;
    try {
      await axios.delete(`${API}/gmail-accounts/${accountId}`, { headers, withCredentials: true });
      toast.success('Account removed');
      fetchGmailAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove account');
    }
  };

  const connectAccount = async (accountId) => {
    setConnectingAccount(accountId);
    try {
      const res = await axios.get(`${API}/oauth/gmail-account/connect/${accountId}?frontend_origin=${encodeURIComponent(window.location.origin)}`, { headers, withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start authorization');
      setConnectingAccount(null);
    }
  };

  const disconnectAccount = async (accountId) => {
    if (!window.confirm('Disconnect this Gmail account? Dealerships using it will lose email access.')) return;
    try {
      await axios.post(`${API}/gmail-accounts/${accountId}/disconnect`, {}, { headers, withCredentials: true });
      toast.success('Account disconnected');
      fetchGmailAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const startEditProfile = () => {
    setProfileForm({ name: user?.name || '', email: user?.email || '', current_password: '', new_password: '', confirm_password: '' });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    if (profileForm.new_password && profileForm.new_password !== profileForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (profileForm.new_password && !profileForm.current_password) {
      toast.error('Enter your current password to change it');
      return;
    }
    setSavingProfile(true);
    try {
      const payload = {};
      if (profileForm.name !== user?.name) payload.name = profileForm.name;
      if (profileForm.email !== user?.email) payload.email = profileForm.email;
      if (profileForm.new_password) {
        payload.current_password = profileForm.current_password;
        payload.new_password = profileForm.new_password;
      }
      const res = await axios.put(`${API}/auth/profile`, payload, { headers, withCredentials: true });
      // Update auth context with new token and user data
      if (res.data.token) {
        localStorage.setItem('auth_token', res.data.token);
        window.location.reload();
      }
      toast.success('Profile updated');
      setEditingProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    }
    setSavingProfile(false);
  };

  const pollAccount = async (accountId) => {
    setConnectingAccount(accountId);
    try {
      const res = await axios.post(`${API}/gmail-accounts/${accountId}/poll`, {}, { headers, withCredentials: true });
      if (res.data.emails_processed > 0) {
        toast.success(`Processed ${res.data.emails_processed} new leads!`);
      } else {
        toast.info(`No new emails found (${res.data.emails_found} total checked)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to poll emails');
    }
    setConnectingAccount(null);
  };

  const clearVdpCache = async () => {
    const vin = cacheVin.trim().toUpperCase();
    if (!vin) return;
    setClearingCache(true);
    try {
      const res = await axios.delete(`${API}/admin/vdp-cache/${vin}`, { headers, withCredentials: true });
      const count = res.data?.deleted_count || 0;
      if (count > 0) {
        toast.success(`Cleared ${count} cache entry${count > 1 ? 's' : ''} for ${vin}`);
      } else {
        toast.info(`No cache entries found for ${vin}`);
      }
      setCacheVin('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear cache');
    }
    setClearingCache(false);
  };

  const clearAllVdpCache = async () => {
    if (!window.confirm('Clear ALL VDP cache entries? This will force re-scraping for every vehicle on the next lead.')) return;
    setClearingAllCache(true);
    try {
      const res = await axios.delete(`${API}/admin/vdp-cache-all`, { headers, withCredentials: true });
      const count = res.data?.deleted_count || 0;
      toast.success(`Cleared ${count} cache entries`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear cache');
    }
    setClearingAllCache(false);
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
          Settings
        </h1>
        <p className="text-slate-400 text-sm mt-2">Configure your My ETA instance</p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
        {/* Profile */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="profile-settings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-500" /> Profile
            </h3>
            {!editingProfile && (
              <Button
                onClick={startEditProfile}
                variant="outline"
                size="sm"
                className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white text-[10px] font-bold tracking-widest uppercase h-8"
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )}
          </div>
          {editingProfile ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Name</Label>
                <Input
                  value={profileForm.name}
                  onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm text-white"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Email</Label>
                <Input
                  type="email"
                  value={profileForm.email}
                  onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm text-white"
                  data-testid="profile-email-input"
                />
              </div>
              <div className="border-t border-[#262626] pt-4 mt-4">
                <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-3">Change Password</p>
                <div className="space-y-3">
                  <div className="relative">
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Current Password</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showCurrentPass ? 'text' : 'password'}
                        value={profileForm.current_password}
                        onChange={e => setProfileForm(f => ({ ...f, current_password: e.target.value }))}
                        placeholder="Enter current password"
                        className="bg-[#111] border-[#333] h-11 text-sm text-white pr-10 placeholder:text-slate-600"
                        data-testid="profile-current-password-input"
                      />
                      <button type="button" onClick={() => setShowCurrentPass(!showCurrentPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                        {showCurrentPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">New Password</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showNewPass ? 'text' : 'password'}
                        value={profileForm.new_password}
                        onChange={e => setProfileForm(f => ({ ...f, new_password: e.target.value }))}
                        placeholder="Enter new password"
                        className="bg-[#111] border-[#333] h-11 text-sm text-white pr-10 placeholder:text-slate-600"
                        data-testid="profile-new-password-input"
                      />
                      <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                        {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={profileForm.confirm_password}
                      onChange={e => setProfileForm(f => ({ ...f, confirm_password: e.target.value }))}
                      placeholder="Confirm new password"
                      className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm text-white placeholder:text-slate-600"
                      data-testid="profile-confirm-password-input"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-9 px-6"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
                <Button
                  onClick={() => setEditingProfile(false)}
                  variant="outline"
                  className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white font-bold text-[10px] tracking-widest uppercase h-9"
                  data-testid="cancel-edit-profile-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Name</p>
                <p className="text-sm text-white mt-1">{user?.name}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Email</p>
                <p className="text-sm text-white mt-1">{user?.email}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Auto-Response */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="notification-settings">
          <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <Bell className="w-4 h-4 text-red-500" /> Auto-Response
            {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-red-500" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-white">Global Auto-Respond</Label>
                </div>
                <Switch 
                  checked={settings.global_auto_respond}
                  onCheckedChange={(checked) => updateSetting('global_auto_respond', checked)}
                  data-testid="auto-respond-switch" 
                />
              </div>
            </div>
          )}
        </Card>
        </div>

        <div className="grid grid-cols-2 gap-6">
        {/* VDP Cache Management */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="vdp-cache-settings">
          <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <Database className="w-4 h-4 text-red-500" /> VDP Cache
          </h3>
          <Input
              value={cacheVin}
              onChange={e => setCacheVin(e.target.value.toUpperCase())}
              placeholder="Enter VIN to clear"
              maxLength={17}
              className="mb-3 bg-[#111] border-[#333] h-11 text-sm text-white font-mono placeholder:text-slate-600 uppercase"
              onKeyDown={e => e.key === 'Enter' && clearVdpCache()}
              data-testid="vdp-cache-vin-input"
            />
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={clearVdpCache}
              disabled={clearingCache || !cacheVin.trim()}
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-11 px-6"
              data-testid="clear-vdp-cache-btn"
            >
              {clearingCache ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Clear VIN'}
            </Button>
            <Button
              onClick={clearAllVdpCache}
              disabled={clearingAllCache}
              variant="outline"
              className="border-[#333] bg-transparent hover:bg-red-500/10 text-slate-400 hover:text-red-400 font-bold text-[10px] tracking-widest uppercase h-11 px-6"
              data-testid="clear-all-vdp-cache-btn"
            >
              {clearingAllCache ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Clear All'}
            </Button>
          </div>
        </Card>

        {/* AI Configuration */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="ai-settings">
          <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-red-500" /> AI Configuration
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-4 text-center">
              <p className="text-sm font-medium text-white">AI Model</p>
              <Badge className="mt-2 bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[9px] font-bold tracking-wider uppercase">
                OPUS-4.6
              </Badge>
            </div>
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-4 text-center">
              <p className="text-sm font-medium text-white">API Key Status</p>
              <Badge className="mt-2 bg-green-500/10 text-green-400 border border-green-500/20 text-[9px] font-bold tracking-wider uppercase">
                Active
              </Badge>
            </div>
            </div>
          </div>
        </Card>
        </div>

        {/* Gmail Processing Accounts */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="gmail-accounts-settings">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setGmailAccountsOpen(!gmailAccountsOpen)} data-testid="gmail-accounts-toggle">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              {gmailAccountsOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              <Server className="w-4 h-4 text-red-500" /> Gmail Processing Accounts
            </h3>
            {gmailAccountsOpen && <Button
              onClick={(e) => { e.stopPropagation(); setShowAddAccount(true); }}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-8"
              data-testid="add-gmail-account-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Account
            </Button>}
          </div>

          {gmailAccountsOpen && <>
          <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3 mb-4 mt-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              Processing accounts are shared Gmail accounts (e.g., <span className="text-slate-200 font-mono">dlr01@etarsp.com</span>) that handle 
              email for multiple dealerships via aliases. Add your Google Workspace accounts here, then assign them to dealerships.
            </p>
          </div>

          {/* Add Account Dialog */}
          <Dialog open={showAddAccount} onOpenChange={setShowAddAccount}>
            <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
                  Add Processing Account
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Account Email</Label>
                  <Input
                    value={newAccountEmail}
                    onChange={e => setNewAccountEmail(e.target.value.toLowerCase().trim())}
                    placeholder="dlr01@etarsp.com"
                    className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600 font-mono"
                    data-testid="new-gmail-account-email-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    The Google Workspace email that will process leads for assigned dealerships
                  </p>
                </div>
                <Button
                  onClick={addGmailAccount}
                  disabled={addingAccount || !newAccountEmail.trim()}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-10"
                  data-testid="confirm-add-gmail-account-btn"
                >
                  {addingAccount ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add Account'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {loadingAccounts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-red-500" />
            </div>
          ) : gmailAccounts.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              <p className="text-sm text-slate-500">No processing accounts configured yet.</p>
              <p className="text-xs text-slate-600 mt-1">Add a Google Workspace account to get started.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="gmail-accounts-list">
              {gmailAccounts.map(acct => (
                <div
                  key={acct.account_id}
                  className="bg-[#111] border border-[#1A1A1A] rounded-lg p-4 hover:border-[#333] transition-colors"
                  data-testid={`gmail-account-${acct.account_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        acct.is_connected ? 'bg-green-500/10' : 'bg-slate-500/10'
                      }`}>
                        <Mail className={`w-4 h-4 ${acct.is_connected ? 'text-green-400' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-mono text-white">{acct.email}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`text-[8px] font-bold tracking-wider uppercase border ${
                            acct.is_connected
                              ? 'bg-green-500/10 text-green-400 border-green-500/20'
                              : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                          }`}>
                            {acct.is_connected ? 'Authorized' : 'Pending Auth'}
                          </Badge>
                          {acct.dealership_count > 0 && (
                            <span className="text-[10px] text-slate-500">
                              {acct.dealership_count} dealership{acct.dealership_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {acct.is_connected ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => pollAccount(acct.account_id)}
                            disabled={connectingAccount === acct.account_id}
                            className="h-8 w-8 text-slate-400 hover:text-white"
                            title="Poll emails"
                            data-testid={`poll-gmail-account-${acct.account_id}`}
                          >
                            {connectingAccount === acct.account_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3.5 h-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => disconnectAccount(acct.account_id)}
                            className="h-8 w-8 text-slate-400 hover:text-orange-400"
                            title="Disconnect"
                            data-testid={`disconnect-gmail-account-${acct.account_id}`}
                          >
                            <Unlink className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => connectAccount(acct.account_id)}
                          disabled={connectingAccount === acct.account_id}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold tracking-wider uppercase h-8"
                          data-testid={`authorize-gmail-account-${acct.account_id}`}
                        >
                          {connectingAccount === acct.account_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Link2 className="w-3 h-3 mr-1" /> Authorize
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteGmailAccount(acct.account_id)}
                        className="h-8 w-8 text-slate-400 hover:text-red-400"
                        title="Remove account"
                        data-testid={`delete-gmail-account-${acct.account_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>}
        </Card>
        {/* Gmail Pub/Sub Push Status */}
        <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="gmail-push-status">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setGmailPushOpen(!gmailPushOpen)} data-testid="gmail-push-toggle">
            <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              {gmailPushOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              <Wifi className="w-4 h-4 text-red-500" /> Gmail Push Status
            </h3>
            {gmailPushOpen && <div className="flex items-center gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); fetchPushHealth(); }}
                size="sm"
                variant="ghost"
                className="h-8 text-slate-400 hover:text-white text-[10px] font-bold tracking-widest uppercase"
                data-testid="refresh-push-health-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
              <Button
                onClick={(e) => { e.stopPropagation(); renewAllWatches(); }}
                disabled={renewingWatches}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-8"
                data-testid="renew-all-watches-btn"
              >
                {renewingWatches ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Renew All</>}
              </Button>
            </div>}
          </div>

          {gmailPushOpen && <>
          <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3 mb-4 mt-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              Real-time Gmail push notifications (Pub/Sub) deliver new leads within seconds — no polling delay.
              Watches expire every 7 days and are auto-renewed in the background. Any account showing a red status
              needs to be reconnected.
            </p>
          </div>

          {loadingPushHealth ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-red-500" />
            </div>
          ) : !pushHealth ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-yellow-500" />
              <p className="text-sm text-slate-400">Couldn't load push status.</p>
              <p className="text-xs text-slate-600 mt-1">Try clicking Refresh.</p>
            </div>
          ) : (pushHealth.accounts || []).length === 0 ? (
            <div className="text-center py-8">
              <WifiOff className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              <p className="text-sm text-slate-500">No connected Gmail accounts yet.</p>
              <p className="text-xs text-slate-600 mt-1">Add and authorize a processing account above.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="push-health-list">
              {pushHealth.accounts.map(a => {
                const daysLeft = a.seconds_left != null ? Math.floor(a.seconds_left / 86400) : null;
                const hoursLeft = a.seconds_left != null ? Math.floor((a.seconds_left % 86400) / 3600) : null;
                const badgeStyles = a.healthy
                  ? 'bg-green-500/10 text-green-400 border-green-500/20'
                  : 'bg-red-500/10 text-red-400 border-red-500/20';
                const fmt = (iso) => {
                  if (!iso) return '—';
                  try {
                    const d = new Date(iso);
                    const diffMs = Date.now() - d.getTime();
                    const diffMin = Math.floor(diffMs / 60000);
                    if (diffMin < 1) return 'just now';
                    if (diffMin < 60) return `${diffMin}m ago`;
                    const diffH = Math.floor(diffMin / 60);
                    if (diffH < 24) return `${diffH}h ago`;
                    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'});
                  } catch { return '—'; }
                };
                return (
                  <div
                    key={a.account_id}
                    className="bg-[#111] border border-[#1A1A1A] rounded-lg p-4"
                    data-testid={`push-health-${a.account_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          a.healthy ? 'bg-green-500/10' : 'bg-red-500/10'
                        }`}>
                          {a.healthy ? (
                            <Wifi className="w-4 h-4 text-green-400" />
                          ) : (
                            <WifiOff className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-mono text-white truncate" data-testid={`push-email-${a.account_id}`}>{a.email}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <Badge className={`text-[8px] font-bold tracking-wider uppercase border ${badgeStyles}`} data-testid={`push-badge-${a.account_id}`}>
                              {a.healthy ? 'Push Active' : a.needs_reauth ? 'Needs Reauth' : a.is_expired ? 'Watch Expired' : 'Issue'}
                            </Badge>
                            {a.healthy && daysLeft != null && (
                              <span className="text-[10px] text-slate-500" data-testid={`push-expiry-${a.account_id}`}>
                                {daysLeft > 0 ? `${daysLeft}d ${hoursLeft}h` : `${hoursLeft}h`} until renewal
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pl-11 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 uppercase tracking-wider text-[9px]">Last push</span>
                        <span className="text-slate-300 font-mono" data-testid={`push-last-${a.account_id}`}>{fmt(a.last_push_at)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 uppercase tracking-wider text-[9px]">Last renewal</span>
                        <span className="text-slate-300 font-mono">{fmt(a.last_renewal_ok_at)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 uppercase tracking-wider text-[9px]">Watch setup</span>
                        <span className="text-slate-300 font-mono">{fmt(a.watch_setup_at)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 uppercase tracking-wider text-[9px]">Expires</span>
                        <span className="text-slate-300 font-mono">
                          {a.watch_expiration ? new Date(a.watch_expiration).toLocaleDateString([], {month:'short', day:'numeric'}) : '—'}
                        </span>
                      </div>
                    </div>

                    {a.last_renewal_error && (
                      <div className="mt-3 ml-11 bg-red-500/5 border border-red-500/20 rounded px-3 py-2" data-testid={`push-error-${a.account_id}`}>
                        <p className="text-[10px] text-red-400 flex items-start gap-1.5">
                          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="font-mono leading-relaxed">{a.last_renewal_error}</span>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </>}
        </Card>
      </div>
    </div>
  );
}
